import * as React from 'react'
import { ArrowDown, ArrowUp, Download, Search } from 'lucide-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'

type Tile = 'At Risk' | 'Engaged' | 'Drifting' | 'Disengaged' | 'New'
type Sort = 'score' | 'last_name' | 'renewal_due_on' | 'change_13_weeks'
type Direction = 'ascending' | 'descending'

interface EngagementRow {
  person_id: number
  name: string
  organization: string | null
  email: string
  level: string
  score: number | null
  band: string
  change_13_weeks: number | null
  renewal_due_on: string | null
  top_signal: string | null
}

interface EngagementResponse {
  engagement_view: {
    as_of: string
    selected: Tile
    horizon: number
    tiles: Record<Tile, number>
    rows: EngagementRow[]
  }
}

const tiles: Tile[] = ['At Risk', 'Engaged', 'Drifting', 'Disengaged', 'New']
const bandVariant: Record<string, BadgeProps['variant']> = {
  Engaged: 'success',
  Drifting: 'warning',
  Disengaged: 'destructive',
  New: 'neutral',
}

function dateString(date: Date) {
  return date.toISOString().slice(0, 10)
}

export function EngagementPage({ onOpenMember }: { onOpenMember: (personId: number) => void }) {
  const [selected, setSelected] = React.useState<Tile>('At Risk')
  const [horizon, setHorizon] = React.useState('90')
  const [search, setSearch] = React.useState('')
  const [level, setLevel] = React.useState<ReadonlySet<string>>(new Set())
  const [renewal, setRenewal] = React.useState<ReadonlySet<string>>(new Set())
  const [sort, setSort] = React.useState<Sort>('score')
  const [direction, setDirection] = React.useState<Direction>('ascending')
  const params = new URLSearchParams({ selected, horizon, sort, direction })
  if (search.trim()) params.set('search', search.trim())
  if (level.size) params.set('membership_level', [...level][0])
  if (renewal.size) {
    const days = Number([...renewal][0])
    const today = new Date()
    params.set('renewal_due_from', dateString(today))
    today.setDate(today.getDate() + days)
    params.set('renewal_due_to', dateString(today))
  }
  const endpoint = `/api/v1/engagement_view?${params.toString()}`
  const query = useQuery({
    queryKey: ['engagement-view', endpoint],
    queryFn: () => getJSON<EngagementResponse>(endpoint),
    placeholderData: keepPreviousData,
    retry: false,
  })
  const view = query.data?.engagement_view

  const toggleSort = (next: Sort) => {
    if (next === sort)
      setDirection((current) => (current === 'ascending' ? 'descending' : 'ascending'))
    else {
      setSort(next)
      setDirection('ascending')
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="flex items-baseline justify-between pb-4">
        <h1 className="text-heading-sm font-semibold text-foreground">Engagement</h1>
        {view && (
          <span className="text-caption text-muted-foreground">Scores as of {view.as_of}</span>
        )}
      </header>
      {view && (
        <Card>
          <CardContent className="grid grid-cols-2 divide-x divide-y divide-border p-0 lg:grid-cols-5 lg:divide-y-0">
            {tiles.map((tile) => (
              <button
                key={tile}
                type="button"
                aria-pressed={selected === tile}
                aria-label={`${tile}, ${view.tiles[tile]} members`}
                onClick={() => setSelected(tile)}
                className={`flex min-h-20 flex-col items-start px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${selected === tile ? 'border-b-2 border-b-primary' : ''}`}
              >
                <span
                  className={`text-caption uppercase tracking-wide ${selected === tile ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {tile}
                </span>
                <span className="mt-1 text-heading-sm font-semibold tabular-nums text-foreground">
                  {view.tiles[tile]}
                </span>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
      <EngagementToolbar
        search={search}
        onSearch={setSearch}
        level={level}
        onLevel={setLevel}
        renewal={renewal}
        onRenewal={setRenewal}
        selected={selected}
        horizon={horizon}
        onHorizon={setHorizon}
        endpoint={endpoint}
      />
      <Card className="mt-4">
        <CardContent className="p-0">
          {query.isLoading ? (
            <EmptyState title="Loading Engagement" />
          ) : query.isError ? (
            <EmptyState title="Engagement unavailable" />
          ) : view?.rows.length === 0 ? (
            <EmptyState title="Members are scored after their first 90 days." />
          ) : (
            <EngagementTable
              rows={view?.rows ?? []}
              sort={sort}
              direction={direction}
              onSort={toggleSort}
              onOpenMember={onOpenMember}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function EngagementToolbar({
  search,
  onSearch,
  level,
  onLevel,
  renewal,
  onRenewal,
  selected,
  horizon,
  onHorizon,
  endpoint,
}: {
  search: string
  onSearch: (value: string) => void
  level: ReadonlySet<string>
  onLevel: (value: Set<string>) => void
  renewal: ReadonlySet<string>
  onRenewal: (value: Set<string>) => void
  selected: Tile
  horizon: string
  onHorizon: (value: string) => void
  endpoint: string
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <div className="relative w-80">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          aria-label="Search engagement members"
          className="pl-9"
          placeholder="Search name, email, or organization…"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
      </div>
      <FilterDropdown
        label="Level"
        emptyLabel="All levels"
        options={[
          { value: 'Professional', label: 'Professional' },
          { value: 'Corporate', label: 'Corporate' },
          { value: 'Individual', label: 'Individual' },
        ]}
        selected={level}
        onChange={onLevel}
      />
      <FilterDropdown
        label="Renewal due"
        emptyLabel="Any renewal date"
        options={[
          { value: '30', label: 'Next 30 days' },
          { value: '60', label: 'Next 60 days' },
          { value: '90', label: 'Next 90 days' },
        ]}
        selected={renewal}
        onChange={onRenewal}
      />
      {selected === 'At Risk' && (
        <div className="flex items-center gap-2">
          <span className="text-caption text-muted-foreground">Renewal within</span>
          <Select value={horizon} onValueChange={onHorizon}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[30, 60, 90, 180].map((days) => (
                <SelectItem key={days} value={String(days)}>
                  {days} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <a className="ml-auto" href={`${endpoint}&format=csv`} download="engagement.csv">
        <Button type="button" variant="outline" size="sm" className="gap-2">
          <Download className="size-3.5" aria-hidden="true" />
          Export CSV
        </Button>
      </a>
    </div>
  )
}

function EngagementTable({
  rows,
  sort,
  direction,
  onSort,
  onOpenMember,
}: {
  rows: EngagementRow[]
  sort: Sort
  direction: Direction
  onSort: (sort: Sort) => void
  onOpenMember: (personId: number) => void
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead
            active={sort === 'last_name'}
            direction={direction}
            onToggle={() => onSort('last_name')}
          >
            Member
          </SortableTableHead>
          <TableHead>Level</TableHead>
          <SortableTableHead
            active={sort === 'score'}
            direction={direction}
            onToggle={() => onSort('score')}
          >
            Score
          </SortableTableHead>
          <SortableTableHead
            active={sort === 'change_13_weeks'}
            direction={direction}
            onToggle={() => onSort('change_13_weeks')}
          >
            13-week trend
          </SortableTableHead>
          <SortableTableHead
            active={sort === 'renewal_due_on'}
            direction={direction}
            onToggle={() => onSort('renewal_due_on')}
          >
            Renewal due
          </SortableTableHead>
          <TableHead>Top signal</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.person_id}
            className="cursor-pointer"
            onClick={() => onOpenMember(row.person_id)}
          >
            <TableCell className="py-2">
              <span className="font-medium text-primary">{row.name}</span>
              {row.organization && (
                <div className="text-caption text-muted-foreground">{row.organization}</div>
              )}
              <div className="text-caption text-muted-foreground">{row.email}</div>
            </TableCell>
            <TableCell className="py-2 text-muted-foreground">{row.level}</TableCell>
            <TableCell className="py-2">
              <div className="flex items-center gap-2">
                <span className="tabular-nums font-medium">{row.score ?? '—'}</span>
                {row.band !== 'New' && <Badge variant={bandVariant[row.band]}>{row.band}</Badge>}
              </div>
            </TableCell>
            <TableCell className="py-2 tabular-nums">
              {row.change_13_weeks === null ? (
                '—'
              ) : row.change_13_weeks > 0 ? (
                <span className="text-success">
                  <ArrowUp className="mr-1 inline size-3.5" aria-hidden="true" />+
                  {row.change_13_weeks}
                </span>
              ) : (
                <span className="text-destructive">
                  <ArrowDown className="mr-1 inline size-3.5" aria-hidden="true" />
                  {row.change_13_weeks}
                </span>
              )}
            </TableCell>
            <TableCell className="py-2 tabular-nums">{row.renewal_due_on ?? '—'}</TableCell>
            <TableCell className="py-2">{row.top_signal ?? ''}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
